import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Users, Sparkles, BookOpen, MessageCircle, Award, FileDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, tone }) {
  const toneMap = {
    amber: "from-amber-50 to-orange-50 border-amber-200 text-amber-900",
    green: "from-emerald-50 to-green-50 border-emerald-200 text-emerald-900",
    purple: "from-purple-50 to-violet-50 border-purple-200 text-purple-900",
    pink: "from-rose-50 to-pink-50 border-rose-200 text-rose-900",
  };
  return (
    <div
      className={`bg-gradient-to-br ${toneMap[tone] || toneMap.amber} rounded-2xl border-2 p-5`}
      data-testid={`insights-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-1.5 text-xs uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-3xl font-extrabold leading-tight">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

export default function PublicClassInsights() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/class-insights/${token}`);
        if (cancelled) return;
        setData(res.data);
        document.title = `Class Insights · ${res.data.class_name || "Lemonade Stand"}`;
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 410) setError(err.response.data?.detail || "This insights link is no longer active.");
        else if (status === 404) setError("This insights link could not be found.");
        else setError("We could not load this report right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-amber-700 font-semibold">Loading class insights…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center px-4">
        <div
          className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-amber-200 p-8 text-center"
          data-testid="insights-error-card"
        >
          <div className="text-5xl mb-3">🔒</div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-slate-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const totals = data.totals || {};
  const completion = data.completion || {};
  const pitch = data.pitch_growth || {};
  const themes = data.top_themes || [];
  const movers = data.top_movers || [];
  const share = data.share || {};
  const expiresAt = share.expires_at ? new Date(share.expires_at) : null;
  const validThrough = expiresAt
    ? expiresAt.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 pb-12">
      {/* Hero */}
      <div
        className="bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 text-white"
        data-testid="insights-hero"
      >
        <div className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
          <div className="text-[11px] uppercase tracking-[0.18em] opacity-90 font-semibold">
            Lemonade Stand · Weekly Class Insights
          </div>
          <h1
            className="text-3xl sm:text-5xl font-black mt-2 mb-2 leading-tight"
            data-testid="insights-class-name"
          >
            {data.class_name || "Class Report"}
          </h1>
          <div className="flex flex-wrap gap-2 items-center text-sm opacity-95">
            <span className="bg-white/20 rounded-full px-3 py-1 font-semibold">
              Last {data.window_days} days
            </span>
            {share.educator_name && (
              <span className="bg-white/20 rounded-full px-3 py-1">
                Shared by {share.educator_name}
              </span>
            )}
            {validThrough && (
              <span
                className="bg-emerald-500/90 rounded-full px-3 py-1 font-semibold"
                data-testid="insights-valid-through"
              >
                ✓ Valid through {validThrough}
              </span>
            )}
            <a
              href={`${API}/public/class-insights/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="bg-white text-amber-700 hover:bg-amber-50 rounded-full px-4 py-1.5 font-bold inline-flex items-center gap-1.5 shadow-md hover:shadow-lg transition-all ml-auto"
              data-testid="insights-pdf-btn"
            >
              <FileDown className="w-4 h-4" />
              Download PDF
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 -mt-6">
        {/* AI Narrative */}
        {data.narrative && (
          <div
            className="bg-white rounded-2xl shadow-lg border-l-4 border-amber-400 p-5 sm:p-6 mb-6"
            data-testid="insights-narrative"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              This week at a glance
            </div>
            <p className="text-base sm:text-lg text-slate-800 leading-relaxed font-medium">
              {data.narrative}
            </p>
          </div>
        )}

        {/* KPI grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <StatCard icon={Users} label="Students" value={totals.students || 0} tone="amber" />
          <StatCard
            icon={BookOpen}
            label="Lessons"
            value={totals.lessons_completed || 0}
            sub={`completed this week`}
            tone="green"
          />
          <StatCard
            icon={Award}
            label="Avg Completion"
            value={`${completion.avg_pct || 0}%`}
            sub={`${completion.completed_lessons || 0} of ${completion.total_possible || 0}`}
            tone="purple"
          />
          <StatCard
            icon={TrendingUp}
            label="Pitch Growth"
            value={`+${pitch.delta || 0}%`}
            sub={`${pitch.avg_first_pct || 0}% → ${pitch.avg_best_pct || 0}%`}
            tone="pink"
          />
        </div>

        {/* Themes */}
        <div
          className="bg-white rounded-2xl shadow-md border border-amber-100 p-5 sm:p-6 mb-5"
          data-testid="insights-themes"
        >
          <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-amber-600" />
            Top Question Themes
          </h2>
          {themes.length === 0 ? (
            <p className="text-slate-500 text-sm italic">
              No common questions tracked this week.
            </p>
          ) : (
            <ul className="space-y-3">
              {themes.map((t, i) => (
                <li
                  key={`${t.label}-${i}`}
                  className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0"
                  data-testid={`insights-theme-${i}`}
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-bold text-slate-900">{t.label}</span>
                      <span className="text-xs text-slate-500">
                        {t.count} {t.count === 1 ? "question" : "questions"}
                      </span>
                    </div>
                    {t.example && (
                      <p className="text-sm text-slate-600 mt-1 italic">"{t.example}"</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Movers */}
        <div
          className="bg-white rounded-2xl shadow-md border border-emerald-100 p-5 sm:p-6 mb-5"
          data-testid="insights-movers"
        >
          <h2 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            Top Pitch Movers
          </h2>
          {movers.length === 0 ? (
            <p className="text-slate-500 text-sm italic">
              No pitch attempts this week — encourage students to try the Pitch Practice page.
            </p>
          ) : (
            <ul className="space-y-2">
              {movers.map((m, i) => (
                <li
                  key={`${m.name}-${i}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl bg-emerald-50/60"
                  data-testid={`insights-mover-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {(m.name || "S").charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 truncate">{m.name}</div>
                      <div className="text-xs text-slate-500">
                        {m.first_pct}% → {m.best_pct}% · {m.attempts} attempts
                      </div>
                    </div>
                  </div>
                  <div className="text-emerald-700 font-extrabold text-lg flex-shrink-0">
                    +{m.delta}%
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 mt-8 px-4 leading-relaxed">
          Token-protected, read-only summary. First names only — no email, last name, or
          internal IDs are shared.
          <br />
          Lemonade Stand · Standards-aligned · Nevada CTE / Perkins V · CCTC
        </div>
      </div>
    </div>
  );
}
